// src/generators/dzial3_funkcje/temat3_kwadratowa/podtemat1_ogolna_types/FindRootsFromGeneralTask.js
import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput, { formatLatexPreview, parseFractionLikeInput } from "../../../../components/inputs/FractionInput";

/* =============================================================
   TASK: FindRootsFromGeneralTask
   Cel: z f(x)=ax^2+bx+c policz miejsca zerowe x1, x2 (zawsze Δ>0).
   Wejście: dwa FractionInput (x1 i x2).
   Fixy:
   - label dla pól (x1/x2) -> poprawny podpis w podglądzie
   - checkAnswer: odporne parsowanie (number | {parsed} | raw string)
   - renderUserAnswer: odporne na kolejność argumentów + "(brak odpowiedzi)"
============================================================= */

/* ---------- helpers ---------- */

function randInt(min, max, exclude = new Set()) {
  const span = max - min + 1;
  let v;
  do { v = Math.floor(Math.random() * span) + min; } while (exclude.has(v));
  return v;
}

// nawias tylko dla ujemnych w DZIAŁANIACH
const par = (v) => (v < 0 ? `(${v})` : `${v}`);

// NWD + uproszczenia ułamka i ładny zapis mieszany do odpowiedzi
function gcd(a, b){ a=Math.abs(a); b=Math.abs(b); while(b){const t=a%b;a=b;b=t;} return a||1; }
function simplifyFrac(n, d){
  const s = Math.sign(n)*Math.sign(d) || 1;
  const an = Math.abs(n), ad = Math.abs(d);
  const g = gcd(an, ad);
  return { n: s*(an/g), d: ad/g };
}
function formatFractionMixed(n, d){
  const s = Math.sign(n) < 0 ? "-" : "";
  const N = Math.abs(n), D = Math.abs(d);
  if (N % D === 0) return `${s}${N/D}`;
  const whole = Math.floor(N/D);
  const rem = N % D;
  if (whole === 0) return `${s}\\dfrac{${N}}{${D}}`;
  return `${s}${whole}\\dfrac{${rem}}{${D}}`;
}

// parser odporny na kształt zwrotu parseFractionLikeInput
function parseSmart(rawIn) {
  const raw = rawIn?.raw ?? rawIn ?? "";
  const r = parseFractionLikeInput ? parseFractionLikeInput(raw) : null;
  if (typeof r === "number") return Number.isFinite(r) ? r : null;
  if (r && typeof r === "object" && r.parsed != null) return Number.isFinite(r.parsed) ? r.parsed : null;

  // awaryjnie: prosty parser (mieszane, zwykłe, dziesiętne)
  let s = String(raw ?? "").trim().replace(/\u2212/g, "-").replace(",", ".");
  if (!s) return null;

  let m = s.match(/^\s*([+-]?\d+)\s+(\d+)\s*\/\s*(\d+)\s*$/);
  if (m) {
    const A = parseInt(m[1],10), B = parseInt(m[2],10), C = parseInt(m[3],10);
    if (!C) return null;
    const frac = B / C;
    return A >= 0 ? (A + frac) : (A - frac);
  }
  m = s.match(/^\s*([+-]?\d+)\s*\/\s*([+-]?\d+)\s*$/);
  if (m) {
    const P = parseInt(m[1],10), Q = parseInt(m[2],10);
    if (!Q) return null;
    return P / Q;
  }
  if (/^[+-]?\d+(\.\d+)?$/.test(s)) return Number(s);
  return null;
}

/* ---------- generator: zawsze Δ>0 ---------- */
export function generateTask() {
  const a = randInt(-5, 5, new Set([0]));
  let r1 = randInt(-6, 6, new Set([0]));
  let r2;
  do { r2 = randInt(-6, 6, new Set([0, r1])); } while (r2 === r1);

  const b = -a * (r1 + r2);
  const c = a * r1 * r2;

  const delta = b*b - 4*a*c;              // = a^2 (r1 - r2)^2
  const sqrtDelta = Math.abs(a) * Math.abs(r1 - r2);

  const x1Frac = simplifyFrac(-b - sqrtDelta, 2*a);
  const x2Frac = simplifyFrac(-b + sqrtDelta, 2*a);

  const x1 = x1Frac.n / x1Frac.d;
  const x2 = x2Frac.n / x2Frac.d;

  return {
    type: "FindRootsFromGeneralTask",
    data: { a, b, c, delta, sqrtDelta, x1, x2, x1Frac, x2Frac }
  };
}

/* ---------- render ---------- */

function formatQuadraticLatex(a, b, c) {
  const term = (coeff, base, omitOne=false) => {
    if (coeff === 0) return "";
    const sign = coeff > 0 ? " + " : " - ";
    const abs = Math.abs(coeff);
    const num = (abs === 1 && omitOne) ? "" : String(abs);
    return `${sign}${num}${base}`;
  };
  const aCoeff = Math.abs(a) === 1 ? (a === -1 ? "-" : "") : String(a);
  let out = `${aCoeff}x^2${term(b,"x",true)}`;
  if (c !== 0) out += (c > 0 ? " + " : " - ") + String(Math.abs(c));
  return out;
}

export function renderTask({ data }) {
  const { a, b, c } = data;
  const body = formatQuadraticLatex(a, b, c);
  return (
    <div style={{ marginTop: 6, marginBottom: 6 }}>
      <div>
        Dana jest funkcja w postaci ogólnej: <InlineMath math={'f(x) = ' + body} />
      </div>
      <div>
        Oblicz miejsca zerowe <InlineMath math={'x_1,\\ x_2'} />.
      </div>
    </div>
  );
}

export function renderInput(_task, userAnswer, onChange, disabled) {
  const merge = (ua) =>
    ua && typeof ua === "object" && !Array.isArray(ua) ? ua : {};
  const norm = (v) =>
    v && typeof v === "object" && "raw" in v ? v : { raw: String(v ?? "") };

  const current = merge(userAnswer);
  const val = {
    x1: norm(current.x1),
    x2: norm(current.x2),
  };

  return (
    <div style={{ display: "grid", gap: 14, marginTop: 6 }}>
      <div style={{ display: "grid", gap: 6 }}>
        <InlineMath math={'x_1 ='} />
        <FractionInput
          value={val.x1}
          onChange={(v) => onChange({ ...merge(userAnswer), x1: v })}
          disabled={disabled}
          label="x_1"
        />
      </div>

      <div style={{ display: "grid", gap: 6 }}>
        <InlineMath math={'x_2 ='} />
        <FractionInput
          value={val.x2}
          onChange={(v) => onChange({ ...merge(userAnswer), x2: v })}
          disabled={disabled}
          label="x_2"
        />
      </div>
    </div>
  );
}

/* ---------- check + answers ---------- */

export function checkAnswer({ data }, userAnswer) {
  try {
    if (!userAnswer) return false;

    const v1 = parseSmart(userAnswer?.x1);
    const v2 = parseSmart(userAnswer?.x2);
    if (v1 == null || v2 == null) return false;

    const given = [v1, v2].sort((p, q) => p - q);
    const corr  = [data.x1, data.x2].sort((p, q) => p - q);

    return Math.abs(given[0] - corr[0]) < 0.01 && Math.abs(given[1] - corr[1]) < 0.01;
  } catch {
    return false;
  }
}

// Odporne na kolejność argumentów + "(brak odpowiedzi)" gdy oba puste
export function renderUserAnswer(arg1, arg2) {
  const looksLikeTask = (x) => x && typeof x === "object" && x.data && typeof x.data === "object";
  const task = looksLikeTask(arg1) ? arg1 : (looksLikeTask(arg2) ? arg2 : { data: {} });
  const userAnswer = looksLikeTask(arg1) ? arg2 : arg1;

  const x1raw = userAnswer?.x1?.raw ?? userAnswer?.x1 ?? "";
  const x2raw = userAnswer?.x2?.raw ?? userAnswer?.x2 ?? "";

  const empty1 = !String(x1raw ?? "").trim();
  const empty2 = !String(x2raw ?? "").trim();

  if (empty1 && empty2) return <i>(brak odpowiedzi)</i>;

  return (
    <div style={{ marginTop: 6, marginBottom: 6 }}>
      <InlineMath math={`x_1 = ${formatLatexPreview(x1raw) || "?"}`} />
      <span style={{ margin: "0 10px" }} />
      <InlineMath math={`x_2 = ${formatLatexPreview(x2raw) || "?"}`} />
    </div>
  );
}

export function renderAnswer({ data }) {
  const pairs = [
    { n: data.x1Frac.n, d: data.x1Frac.d, v: data.x1 },
    { n: data.x2Frac.n, d: data.x2Frac.d, v: data.x2 },
  ].sort((p,q)=>p.v-q.v);

  return (
    <div style={{ marginTop: 6, marginBottom: 6 }}>
      <InlineMath math={`x_1 = ${formatFractionMixed(pairs[0].n, pairs[0].d)},\\quad x_2 = ${formatFractionMixed(pairs[1].n, pairs[1].d)}`} />
    </div>
  );
}

/* ---------- explanation ---------- */

export function renderExplanation({ data }) {
  const { a, b, c, delta, sqrtDelta, x1Frac, x2Frac } = data;
  const twoA = 2 * a;
  const b2 = b * b;
  const fourAC = 4 * a * c;

  return (
    <div style={{ textAlign: "left", lineHeight: 1.7, marginTop: 8 }}>
      <div><strong>Krok 1.</strong> Wzory:</div>
      <BlockMath math={'\\Delta = b^2 - 4ac,\\quad x_{1,2} = \\dfrac{-b \\pm \\sqrt{\\Delta}}{2a}'} />

      <div><strong>Krok 2.</strong> Obliczenie delty:</div>
      <BlockMath math={`\\Delta = ${par(b)}^{2} - 4\\cdot${par(a)}\\cdot${par(c)} = ${b2} - ${fourAC < 0 ? `(${fourAC})` : `${fourAC}`} = ${delta}`} />

      <div><strong>Krok 3.</strong> Podstawienie do wzorów:</div>
      <BlockMath math={`x_1 = \\dfrac{-${par(b)} - \\sqrt{${delta}}}{2\\cdot${par(a)}} = \\dfrac{${-b} - ${sqrtDelta}}{${twoA}} = ${formatFractionMixed(x1Frac.n, x1Frac.d)}`} />
      <BlockMath math={`x_2 = \\dfrac{-${par(b)} + \\sqrt{${delta}}}{2\\cdot${par(a)}} = \\dfrac{${-b} + ${sqrtDelta}}{${twoA}} = ${formatFractionMixed(x2Frac.n, x2Frac.d)}`} />

      <div><strong>Wynik:</strong></div>
      <BlockMath math={`\\{x_1,\\ x_2\\} = \\Big\\{${formatFractionMixed(x1Frac.n, x1Frac.d)},\\; ${formatFractionMixed(x2Frac.n, x2Frac.d)}\\Big\\}`} />
    </div>
  );
}

export default {
  generateTask,
  renderTask,
  renderInput,
  checkAnswer,
  renderUserAnswer,
  renderAnswer,
  renderExplanation,
};
